/**
 * @file UserDetailsContext.tsx
 * @description Context provider for managing user details and authentication.
 */

import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { User } from "firebase/auth";
import {
  signInWithPopup,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { parseFullName } from "parse-full-name";
import { toast } from "react-toastify";

import { auth, googleProvider } from "../config/firebaseConfig";
import config from "../config/config";
import log from "../config/logging";
import { UserDetails, UserDetailsContextType, AuthError } from "../types";
import {
  CONSENT_VERSION,
  CONSENT_METHOD,
  CONSENT_PURPOSE,
} from "../components/common/PrivacyConsentCheckbox";

const UserDetailsContext = createContext<UserDetailsContextType | undefined>(
  undefined
);

export const UserDetailsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConsentModal, setShowConsentModal] = useState(false);

  const isAuthenticated = !!userDetails;

  const refreshUserDetails = useCallback(async () => {
    if (user) {
      try {
        const token = await user.getIdToken();
        const details = await getUserData(user.uid, token);
        setUserDetails(details);
      } catch (error) {
        log.error("Error refreshing user details", { error });
      }
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setUser(user);
      if (user) {
        await refreshUserDetails();
      } else {
        setUserDetails(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [refreshUserDetails]);

  const handleAuthError = (error: AuthError) => {
    log.error("Authentication error", { error });
    const errorMessage = getErrorMessage(error);
    return errorMessage;
  };

  const getErrorMessage = (error: AuthError): string => {
    switch (error.code) {
      case "auth/email-already-in-use":
        return "Questa email è già in uso. Se hai già un account prova ad accedere.";
      case "auth/internal-error":
        return "Errore interno del server. Riprova più tardi.";
      case "auth/invalid-credential":
        return "Email o password non corrette.";
      case "auth/invalid-email":
        return "Inserisci un indirizzo email valido.";
      case "auth/weak-password":
        return "La password fornita è troppo debole. Usa una password più forte.";
      case "auth/user-disabled":
        return "Questo account è stato disabilitato. Contatta il supporto per assistenza.";
      case "auth/user-not-found":
        return "Non esiste un account associato a questa email. Verifica l'email o registrati.";
      case "auth/wrong-password":
        return "Password non corretta. Riprova o usa 'Password dimenticata'.";
      case "auth/timeout":
        return "Timeout della richiesta. Riprova più tardi.";
      case "auth/too-many-requests":
        return "Troppi tentativi di accesso. Riprova più tardi.";
      case "auth/network-request-failed":
        return "Errore di rete. Controlla la tua connessione e riprova.";
      case "auth/popup-closed-by-user":
        return "Il popup di autenticazione è stato chiuso. Riprova.";
      case "auth/cancelled-popup-request":
        return "La richiesta di autenticazione è stata annullata. Riprova.";
      case "auth/unauthorized-domain":
        return "Autenticazione non consentita da questo dominio. Contatta il supporto.";
      case "auth/operation-not-allowed":
        return "Questa operazione non è consentita. Contatta il supporto.";
      case "auth/requires-recent-login":
        return "Per favore, effettua nuovamente il login e riprova.";
      case "email-check-failed":
        if ((error as any).cause === "disposable") {
          return "Quello inserito sembra un indirizzo email usa e getta. Per favore utilizza il tuo indirizzo email aziendale.";
        } else if ((error as any).cause === "free") {
          return "Quello inserito sembra un indirizzo email gratuito. Per favore utilizza il tuo indirizzo email aziendale.";
        }
        return "Si è verificato un errore durante la verifica dell'email. Assicurati di utilizzare un indirizzo email valido.";
      default:
        return "Si è verificato un errore imprevisto durante l'autenticazione. Per favore, riprova tra qualche minuto. Se il problema persiste, contatta il supporto.";
    }
  };

  const handleAuthenticationSuccess = async (user: User) => {
    log.debug("Authentication successful, handling user data", {
      userId: user.uid,
    });
    try {
      const token = await user.getIdToken();
      const userData = await getUserData(user.uid, token);
      log.debug("User data fetched", { userData: !!userData });

      if (userData) {
        setUser(user);
        setUserDetails(userData);

        if (needsConsentUpdate(userData)) {
          log.debug("User needs consent update, showing modal");
          setShowConsentModal(true);
        } else {
          log.debug("User consent is up to date");
        }
      } else {
        log.error("No user data received after authentication");
        await signOut(auth);
        setUser(null);
        setUserDetails(null);
        throw new Error("Authentication failed: No user data received");
      }
    } catch (error) {
      log.error("Error handling authentication success", { error });
      await signOut(auth);
      setUser(null);
      setUserDetails(null);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    log.debug("Starting Google sign-in process");
    try {
      const result = await signInWithPopup(auth, googleProvider);
      log.debug("Google sign-in successful", { email: result.user.email });
      const token = await result.user.getIdToken();

      await updateUserDetailsFromProvider(result.user, token);

      const userDetails = await getUserData(result.user.uid, token);
      setUserDetails(userDetails);

      await handleAuthenticationSuccess(result.user);
    } catch (error) {
      log.error("Google sign-in error", { error });
      await signOut(auth);
      setUser(null);
      setUserDetails(null);
      const errorMessage = handleAuthError(error as AuthError);
      toast.error(errorMessage);
    }
  };

  const signInWithEmailPassword = async (email: string, password: string) => {
    log.debug("Starting email/password sign-in", { email });
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      log.debug("Email/password sign-in successful", {
        email: result.user.email,
      });
      await handleAuthenticationSuccess(result.user);
    } catch (error) {
      log.error("Email/password sign-in error", { error });
      await signOut(auth);
      const errorMessage = handleAuthError(error as AuthError);
      toast.error(errorMessage);
      throw error;
    }
  };

  const signUpWithEmailPassword = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    privacyConsent: boolean
  ) => {
    log.debug("Starting email/password sign-up", { email });
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      log.debug("Email/password sign-up successful", {
        email: result.user.email,
      });

      // Create user in Firestore with consent data
      const token = await result.user.getIdToken();
      await createUserInFirestore(
        result.user.uid,
        {
          email,
          firstName,
          lastName,
          photoURL: result.user.photoURL || "",
          consentGiven: privacyConsent,
          consentDate: new Date().toISOString(),
          consentVersion: CONSENT_VERSION,
          consentMethod: CONSENT_METHOD,
          consentPurpose: CONSENT_PURPOSE,
        },
        token
      );

      await handleAuthenticationSuccess(result.user);
    } catch (error) {
      log.error("Email/password sign-up error", { error });
      await signOut(auth);
      const errorMessage = handleAuthError(error as AuthError);
      toast.error(errorMessage);
      throw error;
    }
  };

  const createUserInFirestore = async (
    uid: string,
    userData: Omit<
      UserDetails,
      "remainingContents" | "contentLimit" | "isPremium"
    >,
    token: string
  ) => {
    try {
      const response = await fetch(`${config.backendUrl}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error("Failed to create user in Firestore");
      }
    } catch (error) {
      log.error("Error creating user in Firestore", { error });
      throw error;
    }
  };

  const getUserData = async (
    uid: string,
    token: string
  ): Promise<UserDetails | null> => {
    try {
      const response = await fetch(`${config.backendUrl}/api/users/${uid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.user.isPremium) {
          data.user.contentLimit = Infinity;
          data.user.remainingContents = Infinity;
        }
        return data.user;
      }
      return null;
    } catch (error) {
      log.error("Error fetching user data", { error });
      return null;
    }
  };

  const needsConsentUpdate = (userData: UserDetails) => {
    return !(
      userData.consentGiven && userData.consentVersion === CONSENT_VERSION
    );
  };

  const updateUserDetailsFromProvider = async (user: User, token: string) => {
    log.debug("Updating user details from provider", { userId: user.uid });
    const { first: firstName, last: lastName } = parseFullName(
      user.displayName || ""
    );

    try {
      const response = await fetch(`${config.backendUrl}/api/users`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: user.email,
          firstName: firstName || "",
          lastName: lastName || "",
          photoURL: user.photoURL || "",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === "email-check-failed") {
          const error = new Error(errorData.message);
          (error as any).code = errorData.code;
          (error as any).cause = errorData.cause;
          throw error;
        }
        log.error("Error updating user details", { errorData });
        throw new Error(errorData.message || "Failed to update user details");
      }

      log.debug("User details updated successfully");
    } catch (error) {
      log.error("Error updating user details from provider", { error });
      throw error;
    }
  };

  const updateUserConsent = async (token: string, userId: string) => {
    log.debug("Updating user consent", { userId });
    try {
      const response = await fetch(`${config.backendUrl}/api/users/${userId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          consentGiven: true,
          consentDate: new Date().toISOString(),
          consentVersion: CONSENT_VERSION,
          consentMethod: CONSENT_METHOD,
          consentPurpose: CONSENT_PURPOSE,
        }),
      });

      if (!response.ok) {
        throw new Error("Error updating user consent");
      }
    } catch (error) {
      log.error("Error updating user consent", { error });
      throw error;
    }
  };

  const handleUserConsentUpdate = async () => {
    if (!user) {
      log.error("Attempting to update user consent without logged in user");
      throw new Error("No user logged in");
    }

    log.debug("Updating user consent", { userId: user.uid });
    try {
      const token = await user.getIdToken();
      await updateUserConsent(token, user.uid);
      setShowConsentModal(false);
      await refreshUserDetails();
      log.debug("User consent updated successfully");
      toast.success("Consenso privacy aggiornato");
    } catch (error) {
      log.error("Error updating user consent", { error });
      toast.error(
        "Errore durante l'aggiornamento del consenso privacy. Riprova più tardi."
      );
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserDetails(null);
    } catch (error) {
      log.error("Error during logout", { error });
      toast.error("Errore durante il logout. Riprova più tardi.");
      throw error;
    }
  };

  const isUserConsentValid = (): boolean => {
    return !!(
      userDetails?.consentGiven &&
      userDetails?.consentVersion === CONSENT_VERSION
    );
  };

  return (
    <UserDetailsContext.Provider
      value={{
        user,
        userDetails,
        isAuthenticated,
        loading,
        showConsentModal,
        signInWithGoogle,
        signInWithEmailPassword,
        signUpWithEmailPassword,
        logout,
        refreshUserDetails,
        updateUserConsent: handleUserConsentUpdate,
        isUserConsentValid,
        setShowConsentModal,
        handleAuthError,
      }}
    >
      {children}
    </UserDetailsContext.Provider>
  );
};

export const useUserDetails = () => {
  const context = useContext(UserDetailsContext);
  if (context === undefined) {
    throw new Error("useUserDetails must be used within a UserDetailsProvider");
  }
  return context;
};
